import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';

const CompanionGender: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedGender, setSelectedGender] = useState<string>('');

  const genderOptions = [
    {
      value: 'male',
      label: t('companions.male', 'Male'),
      emoji: '👨',
      description: t('companionCreation.masculineTraits', 'Masculine personality traits'),
    },
    {
      value: 'female',
      label: t('companions.female', 'Female'),
      emoji: '👩',
      description: t('companionCreation.feminineTraits', 'Feminine personality traits'),
    },
    {
      value: 'non-binary',
      label: t('companions.nonBinary', 'Non-binary'),
      emoji: '🌈',
      description: t('companionCreation.nonBinaryTraits', 'Non-binary personality traits'),
    },
  ];

  const handleContinue = () => {
    if (selectedGender) {
      sessionStorage.setItem('companionGender', selectedGender);
      navigate('/companion/relationship');
    }
  };

  const handleSkip = () => {
    sessionStorage.setItem('companionGender', 'non-binary');
    navigate('/companion/relationship');
  };

  return (
    <div className="min-h-screen bg-dark-bg dark:bg-dark-bg bg-light-bg flex flex-col">
      <div className="mobile-container flex-1 flex flex-col">
        {/* Header */}
        <div className="pt-safe px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface"
          >
            <ArrowLeftIcon className="w-5 h-5 text-dark-text dark:text-dark-text text-light-text" />
          </button>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col px-6 pb-8">
          {/* Progress indicator - 5 steps, step 2 active */}
          <div className="flex space-x-2 mb-8">
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-dark-text dark:text-dark-text text-light-text mb-2">
            {t('companionCreation.genderTitle', "Choose your companion's personality type")}
          </h1>
          <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-8">
            {t('companionCreation.genderSubtitle', 'This helps shape their communication style')}
          </p>

          {/* Gender options */}
          <div className="space-y-3 mb-auto">
            {genderOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedGender(option.value)}
                className={`w-full p-4 rounded-2xl border-2 transition-all ${
                  selectedGender === option.value
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-dark-border dark:border-dark-border border-light-border bg-dark-surface dark:bg-dark-surface bg-white hover:border-dark-text-secondary dark:hover:border-dark-text-secondary hover:border-gray-400'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <span className="text-3xl">{option.emoji}</span>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-dark-text dark:text-dark-text text-light-text">
                      {option.label}
                    </div>
                    <div className="text-sm text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                      {option.description}
                    </div>
                  </div>
                  {selectedGender === option.value && (
                    <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleContinue}
              disabled={!selectedGender}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.continue', 'Continue')}
            </button>
            <button onClick={handleSkip} className="btn-secondary">
              {t('companionCreation.skipForNow', 'Skip for now')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanionGender;
